import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { useBoardTheme } from "./boardThemeStore";
import { GLOBAL_PHASES } from "./expansionBudgetReportData";
import {
  aggregateMaterialsInventoryByItem,
  aggregateMaterialsInventoryByProject,
  buildMaterialsInventoryRows,
  filterMaterialsInventoryRows,
  formatInventoryQuantity,
  listMaterialItemOptions,
  summarizeMaterialsInventory,
} from "./materialsInventoryReportData";
import {
  canAccessMaterialsInventory,
  materialsInventoryPluginEnabled,
} from "./materialsInventoryAccess";
import "./materialsInventoryPage.css";

ModuleRegistry.registerModules([AllCommunityModule]);

function PhaseFilter({ selected, onChange }) {
  const toggle = (phase) => {
    const set = new Set(selected);
    if (set.has(phase)) set.delete(phase);
    else set.add(phase);
    onChange([...set]);
  };

  return (
    <div className="materials-inv-phase-list" role="group" aria-label="Filter by phase">
      {GLOBAL_PHASES.map((phase) => (
        <button
          key={phase}
          type="button"
          className={`materials-inv-phase-chip${selected.includes(phase) ? " is-selected" : ""}`}
          onClick={() => toggle(phase)}
        >
          {phase}
        </button>
      ))}
    </div>
  );
}

function quantityValueFormatter(params) {
  return formatInventoryQuantity(params.value, params.data?.unit);
}

function csvEscapeCell(val) {
  if (val == null) return "";
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function getDisplayedExportColumnDefs(api, fallbackColumnDefs) {
  const displayed = api?.getAllDisplayedColumns?.() ?? [];
  if (displayed.length) {
    return displayed
      .map((col) => ({
        ...col.getColDef(),
        colId: col.getColId(),
        field: col.getColDef().field,
      }))
      .filter((def) => def.field);
  }
  return (fallbackColumnDefs || []).filter((c) => c.field && c.hide !== true);
}

function collectDisplayedGridRows(api) {
  const rows = [];
  api?.forEachNodeAfterFilterAndSort?.((node) => {
    if (node?.data) rows.push(node.data);
  });
  return rows;
}

function exportMaterialsRowsToCsv(rows, columnDefs, filename) {
  const cols = (columnDefs || []).filter((c) => c.field);
  const headerLine = cols.map((c) => csvEscapeCell(c.headerName || c.field || "")).join(",");
  const lines = [headerLine];
  for (const row of rows) {
    const cells = cols.map((c) => {
      let v = row[c.field];
      if (typeof c.valueFormatter === "function") {
        try {
          v = c.valueFormatter({ value: v, data: row });
        } catch {
          /* keep raw value */
        }
      }
      return csvEscapeCell(v);
    });
    lines.push(cells.join(","));
  }
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const SUMMARY_GRID_COLUMNS = [
  {
    field: "materialItem",
    headerName: "Material",
    flex: 1,
    minWidth: 200,
    sort: "asc",
  },
  {
    field: "unit",
    headerName: "Unit",
    width: 100,
  },
  {
    field: "quantity",
    headerName: "Total",
    width: 130,
    type: "numericColumn",
    valueFormatter: quantityValueFormatter,
  },
  {
    field: "projectCount",
    headerName: "Projects",
    width: 105,
    type: "numericColumn",
  },
  {
    field: "lineCount",
    headerName: "Lines",
    width: 90,
    type: "numericColumn",
  },
];

const PROJECT_GRID_COLUMNS = [
  {
    field: "workOrder",
    headerName: "Work order",
    width: 140,
    cellRenderer: WorkOrderCell,
  },
  {
    field: "projectName",
    headerName: "Project",
    flex: 1,
    minWidth: 180,
  },
  {
    field: "phase",
    headerName: "Phase",
    width: 140,
  },
  {
    field: "owner",
    headerName: "Owner",
    width: 140,
  },
  {
    field: "quantity",
    headerName: "Amount",
    width: 130,
    type: "numericColumn",
    valueFormatter: quantityValueFormatter,
  },
  {
    field: "lineCount",
    headerName: "Lines",
    width: 90,
    type: "numericColumn",
  },
];

function WorkOrderCell(params) {
  const wo = String(params.value || "").trim();
  const projectId = params.data?.projectId;
  const label = wo || params.data?.projectName || "Open";
  if (projectId == null || projectId === "") return label;
  return (
    <Link to={`/project/${projectId}`} className="grid-cell-link materials-inv-project-link">
      {label}
    </Link>
  );
}

function MaterialsInventoryPanel({ apiUrl, getAuthHeaders, portfolio }) {
  const { agGridTheme } = useBoardTheme();
  const summaryGridApiRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [materialFilter, setMaterialFilter] = useState("");
  const [search, setSearch] = useState("");
  const [displayedExportCount, setDisplayedExportCount] = useState(0);

  const syncDisplayedExportCount = useCallback((api) => {
    const gridApi = api || summaryGridApiRef.current;
    if (!gridApi) {
      setDisplayedExportCount(0);
      return;
    }
    setDisplayedExportCount(collectDisplayedGridRows(gridApi).length);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = getAuthHeaders?.() || {};
      const { data: projects } = await axios.get(`${apiUrl}/projects`, { headers });
      setAllRows(buildMaterialsInventoryRows(projects));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not load materials inventory.");
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const portfolioRows = useMemo(
    () => allRows.filter((row) => row.portfolio === portfolio),
    [allRows, portfolio]
  );

  const materialOptions = useMemo(() => listMaterialItemOptions(portfolioRows), [portfolioRows]);

  const filteredRows = useMemo(
    () =>
      filterMaterialsInventoryRows(portfolioRows, {
        phases: selectedPhases,
        materialItem: materialFilter,
        search,
      }),
    [portfolioRows, selectedPhases, materialFilter, search]
  );

  const summary = useMemo(() => summarizeMaterialsInventory(filteredRows), [filteredRows]);
  const summaryGridRows = useMemo(
    () => aggregateMaterialsInventoryByItem(filteredRows),
    [filteredRows]
  );
  const projectGridRows = useMemo(
    () => aggregateMaterialsInventoryByProject(filteredRows),
    [filteredRows]
  );
  const showProjects = Boolean(String(materialFilter || "").trim());
  const gridRows = showProjects ? projectGridRows : summaryGridRows;
  const gridColumns = useMemo(() => {
    if (!showProjects) return SUMMARY_GRID_COLUMNS;
    const cols = PROJECT_GRID_COLUMNS.map((c) => ({ ...c }));
    if (portfolio === "expansion") {
      cols.splice(2, 0, {
        field: "segment",
        headerName: "Segment",
        width: 140,
      });
    }
    return cols;
  }, [showProjects, portfolio]);
  const selectedTotal = summaryGridRows[0];

  useEffect(() => {
    syncDisplayedExportCount();
  }, [gridRows, syncDisplayedExportCount]);

  const handleExport = useCallback(() => {
    const api = summaryGridApiRef.current;
    const rows = collectDisplayedGridRows(api);
    const cols = getDisplayedExportColumnDefs(api, gridColumns);
    const suffix = showProjects ? "by-project" : "by-item";
    exportMaterialsRowsToCsv(rows, cols, `materials-${portfolio}-${suffix}.csv`);
  }, [portfolio, gridColumns, showProjects]);

  const portfolioLabel = portfolio === "expansion" ? "Expansion" : "Commercial";

  return (
    <div className="materials-inv-panel">
      <div className="materials-inv-filters">
        <div className="materials-inv-filter">
          <label htmlFor={`materials-inv-search-${portfolio}`}>Search</label>
          <input
            id={`materials-inv-search-${portfolio}`}
            type="search"
            placeholder="Work order, project, material…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="materials-inv-filter">
          <label htmlFor={`materials-inv-material-${portfolio}`}>Material</label>
          <select
            id={`materials-inv-material-${portfolio}`}
            value={materialFilter}
            onChange={(e) => setMaterialFilter(e.target.value)}
          >
            <option value="">All materials</option>
            {materialOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div className="materials-inv-filter materials-inv-filter--wide">
          <label>Phases</label>
          <PhaseFilter selected={selectedPhases} onChange={setSelectedPhases} />
        </div>
      </div>

      {loading ? <p className="materials-inv-status">Loading {portfolioLabel.toLowerCase()} materials…</p> : null}
      {error ? <p className="materials-inv-status materials-inv-status--error">{error}</p> : null}

      {!loading && !error ? (
        <>
          <div className="materials-inv-kpis">
            {showProjects && selectedTotal ? (
              <div className="materials-inv-kpi">
                <div className="materials-inv-kpi-label">Total</div>
                <div className="materials-inv-kpi-value">
                  {formatInventoryQuantity(selectedTotal.quantity, selectedTotal.unit)}
                </div>
              </div>
            ) : null}
            <div className="materials-inv-kpi">
              <div className="materials-inv-kpi-label">Material lines</div>
              <div className="materials-inv-kpi-value">{summary.lineCount}</div>
            </div>
            <div className="materials-inv-kpi">
              <div className="materials-inv-kpi-label">Projects</div>
              <div className="materials-inv-kpi-value">{summary.projectCount}</div>
            </div>
            {!showProjects ? (
              <div className="materials-inv-kpi">
                <div className="materials-inv-kpi-label">Unique materials</div>
                <div className="materials-inv-kpi-value">{summary.uniqueMaterials}</div>
              </div>
            ) : null}
            {summary.reviewPendingCount > 0 ? (
              <div className="materials-inv-kpi materials-inv-kpi--warn">
                <div className="materials-inv-kpi-label">Needs review</div>
                <div className="materials-inv-kpi-value">{summary.reviewPendingCount}</div>
              </div>
            ) : null}
          </div>

          <div className="materials-inv-section-heading">
            <h3 className="materials-inv-section-title">
              {showProjects ? `Projects with ${materialFilter}` : "Totals by material"}
            </h3>
            <button
              type="button"
              className="materials-inv-export-link"
              disabled={displayedExportCount === 0}
              onClick={handleExport}
            >
              Export ({displayedExportCount})
            </button>
          </div>
          <div className="materials-inv-grid-wrap">
            <AgGridReact
              key={showProjects ? "projects" : "materials"}
              theme={agGridTheme}
              rowData={gridRows}
              columnDefs={gridColumns}
              getRowId={(params) => params.data.id}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 80,
              }}
              domLayout="autoHeight"
              suppressCellFocus
              onGridReady={(params) => {
                summaryGridApiRef.current = params.api;
                syncDisplayedExportCount(params.api);
              }}
              onFilterChanged={(params) => syncDisplayedExportCount(params.api)}
              onSortChanged={(params) => syncDisplayedExportCount(params.api)}
              onModelUpdated={(params) => syncDisplayedExportCount(params.api)}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

export function MaterialsInventoryPage({ apiUrl, getAuthHeaders }) {
  const [activePane, setActivePane] = useState("commercial");

  return (
    <div className="materials-inv-shell">
      <div className="materials-inv-page">
        <header className="materials-inv-page-header">
          <h1 className="materials-inv-page-title">Materials inventory</h1>
          <p className="materials-inv-page-lead">
            Portfolio-wide totals of engineering permit materials across commercial and expansion
            projects. Use this view for procurement planning. Lines flagged for review came from
            permit-plan extraction and have not been verified yet.
          </p>
        </header>

        <div className="materials-inv-tabs" role="tablist" aria-label="Materials portfolio">
          <button
            type="button"
            role="tab"
            aria-selected={activePane === "commercial"}
            className={`materials-inv-tab${activePane === "commercial" ? " is-active" : ""}`}
            onClick={() => setActivePane("commercial")}
          >
            Commercial
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activePane === "expansion"}
            className={`materials-inv-tab${activePane === "expansion" ? " is-active" : ""}`}
            onClick={() => setActivePane("expansion")}
          >
            Expansion
          </button>
        </div>

        {activePane === "commercial" ? (
          <MaterialsInventoryPanel
            apiUrl={apiUrl}
            getAuthHeaders={getAuthHeaders}
            portfolio="commercial"
          />
        ) : (
          <MaterialsInventoryPanel
            apiUrl={apiUrl}
            getAuthHeaders={getAuthHeaders}
            portfolio="expansion"
          />
        )}
      </div>
    </div>
  );
}

export function MaterialsInventoryGate({ apiUrl, getAuthHeaders }) {
  if (!materialsInventoryPluginEnabled()) {
    return <Navigate to="/" replace />;
  }
  if (!canAccessMaterialsInventory()) {
    return <Navigate to="/" replace />;
  }
  return <MaterialsInventoryPage apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />;
}

export default MaterialsInventoryGate;
